/**
 * Client-side CSV for the §F6 result panels — Part H's "`CSV` (client-generated from
 * result)".
 *
 * The rows are already in the browser: §H1's series, §H3's zonal table, §H4's ranking.
 * Asking the server for them again would be a second query answering the same question,
 * and — because §E7 caches by params — one that could answer it from a *different* run.
 * So the file is built from the payload the panel is showing, which is the only way the
 * table and the download can be guaranteed to agree.
 *
 * The mockup's `downloadFile()`, with the quoting it never needed on demo data: a unit
 * called `Kadiogo, Centre` and one called `Sud-Ouest "B"` both exist in the hierarchy, and
 * either would silently shift every column after it.
 */

/** RFC 4180 quoting: wrap in quotes and double any quote inside. */
export function csvCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return /["\n,;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCsv(header: readonly string[], rows: readonly (readonly unknown[])[]): string {
  return [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\n');
}

/**
 * Hand the browser a file. A blob URL and a synthetic click, revoked on the next tick —
 * there is no server route for a table that only exists in this tab.
 */
export function downloadCsv(filename: string, text: string): void {
  // The BOM is what makes Excel open a UTF-8 CSV as UTF-8: without it, `Boucle du
  // Mouhoun` and every French label in the file arrive mojibaked on a Windows desktop,
  // which is most of this programme's readers.
  const blob = new Blob([`\uFEFF${text}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
